import React from "react";
import { useNavigate } from "react-router-dom";

import { Cog } from "../../../components/icons/svg/outline";
import {
  getProjectCustomerLabel,
  getProjectTitle,
} from "../../../utils/business";
import { getUserDisplayName } from "../../../utils/business/user";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
} from "../shared/projectTicketSummaries";
import {
  ProjectRfiSummaryTile,
  ProjectTmSummaryTile,
} from "./CompactTicketSummary";
import {
  getProjectStatusConfig,
  PROJECT_HOME_CHIP_CLASS,
  PROJECT_HOME_MANAGER_CHIP_CLASS,
} from "./projectHomeHelpers";

/** List-view chips are one step smaller than the previous ``text-sm`` chrome. */
const LIST_CHIP_CLASS = `${PROJECT_HOME_CHIP_CLASS} h-5 text-xs`;

/** Shared grid for header + rows (T&M / RFI sit between Customer and Managers). */
const LIST_GRID_CLASS =
  "sm:grid-cols-[4.5rem_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto_auto]";

/**
 * Compact manager pills for a project list row.
 *
 * @param {Object} props
 * @param {Array<Object>} props.managers - Project manager user rows.
 * @returns {JSX.Element}
 */
function ManagerPills({ managers }) {
  if (!managers?.length) {
    return (
      <span
        className={`${LIST_CHIP_CLASS} whitespace-nowrap border-gray-300 bg-gray-100 text-gray-500`}
      >
        No manager
      </span>
    );
  }

  return (
    <div className="flex min-w-0 items-center justify-start gap-0.5 overflow-hidden">
      {managers.map((manager) => {
        const name = getUserDisplayName(manager, "");
        if (!name) return null;
        return (
          <span
            key={manager.id || name}
            title={name}
            className={`${LIST_CHIP_CLASS} max-w-[7.5rem] truncate whitespace-nowrap ${PROJECT_HOME_MANAGER_CHIP_CLASS}`}
          >
            {name}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Single project row for the Projects Home list view.
 *
 * @param {Object} props
 * @param {Object} props.project - Project API row.
 * @param {(project: Object) => void} [props.onEdit] - Opens project edit modal.
 * @param {Object} [props.tmSummary] - Compact T&M status summary for this project.
 * @param {Object} [props.rfiSummary] - Compact RFI status summary for this project.
 * @param {boolean} [props.ticketsLoading=false] - Whether ticket lists are still loading.
 * @param {boolean} [props.showZeroMetrics=false] - Keep zero-count ticket pills visible.
 * @returns {JSX.Element}
 */
function ProjectListRow({
  project,
  onEdit,
  tmSummary = emptyTmCardSummary(),
  rfiSummary = emptyRfiCardSummary(),
  ticketsLoading = false,
  showZeroMetrics = false,
}) {
  const navigate = useNavigate();
  const status = getProjectStatusConfig(project);
  const StatusIcon = status.Icon;
  const projectNumber =
    project.number || project.project_number || `Project ${project.id}`;
  const projectTitle = getProjectTitle(project);
  const projectPath = `/projects/${project.id}`;

  /**
   * Open the project page when the row is activated.
   *
   * @returns {void}
   */
  const openProject = () => {
    navigate(projectPath);
  };

  return (
    <li className="group border-b border-gray-100 last:border-b-0">
      <div
        role="link"
        tabIndex={0}
        aria-label={`View ${projectTitle}`}
        className={`grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 px-2.5 py-1.5 transition hover:bg-secondary-50/40 sm:min-h-9 ${LIST_GRID_CLASS} sm:gap-2 sm:px-3 sm:py-1`}
        onClick={openProject}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openProject();
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-1.5 sm:contents">
          <span className="flex-shrink-0 text-sm font-semibold leading-none tabular-nums text-primary">
            {projectNumber}
          </span>
          <span className="min-w-0 truncate text-sm font-normal leading-none tracking-tight text-gray-900">
            {projectTitle}
          </span>
        </div>

        <div className="col-span-2 min-w-0 truncate text-sm font-normal leading-none text-gray-600 sm:col-span-1 sm:text-gray-700">
          {getProjectCustomerLabel(project)}
        </div>

        <div
          className="col-span-2 min-w-0 sm:col-span-1"
          aria-label={`T&M for ${projectTitle}`}
        >
          <ProjectTmSummaryTile
            summary={tmSummary}
            isLoading={ticketsLoading}
            showZeroMetrics={showZeroMetrics}
          />
        </div>

        <div
          className="col-span-2 min-w-0 sm:col-span-1"
          aria-label={`RFI for ${projectTitle}`}
        >
          <ProjectRfiSummaryTile
            summary={rfiSummary}
            isLoading={ticketsLoading}
            showZeroMetrics={showZeroMetrics}
          />
        </div>

        <div className="col-span-2 min-w-0 sm:col-span-1">
          <ManagerPills managers={project.managers || []} />
        </div>

        <span
          className={`${LIST_CHIP_CLASS} justify-self-start whitespace-nowrap ${status.className}`}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          {status.label}
        </span>

        <div className="flex h-full flex-shrink-0 items-center justify-start">
          {onEdit ? (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-secondary-100"
              aria-label={`Edit ${projectTitle}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEdit(project);
              }}
            >
              <Cog className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * List view of projects for the Projects landing page.
 *
 * @param {Object} props
 * @param {Array<Object>} props.projects - Visible project API rows.
 * @param {(project: Object) => void} [props.onEdit] - Opens project edit modal.
 * @param {Map<string, { tm: Object, rfi: Object }>} [props.ticketSummariesByProjectId] - Per-project ticket summaries.
 * @param {boolean} [props.ticketsLoading=false] - Whether ticket lists are still loading.
 * @param {boolean} [props.showZeroMetrics=false] - Keep zero-count ticket pills visible.
 * @returns {JSX.Element}
 */
const ProjectList = ({
  projects,
  onEdit,
  ticketSummariesByProjectId,
  ticketsLoading = false,
  showZeroMetrics = false,
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm shadow-gray-300">
    <div
      className={`hidden h-8 ${LIST_GRID_CLASS} items-center gap-2 bg-primary px-3 text-sm font-normal uppercase tracking-wide text-primary-100 sm:grid`}
    >
      <span className="text-white">No.</span>
      <span>Project</span>
      <span>Customer</span>
      <span>T&M</span>
      <span>RFI</span>
      <span>Managers</span>
      <span>Status</span>
      <span>Edit</span>
    </div>
    <ul>
      {projects.map((project) => {
        const summaries = ticketSummariesByProjectId?.get(String(project.id));
        return (
          <ProjectListRow
            key={project.id}
            project={project}
            onEdit={onEdit}
            tmSummary={summaries?.tm || emptyTmCardSummary()}
            rfiSummary={summaries?.rfi || emptyRfiCardSummary()}
            ticketsLoading={ticketsLoading}
            showZeroMetrics={showZeroMetrics}
          />
        );
      })}
    </ul>
  </div>
);

export default ProjectList;
